import { Edit, Eye, Trash2 } from 'lucide-react';
import type { Country } from '../types/countries.types';
import { useAuthorization } from '@/core/hooks/useAuthorization';
import Protected from '@/shared/components/ahkinia/Protected/Protected';
import { Switch } from '@/shared/components/ahkinia/Switch/switch';
import { Button } from '@/shared/components/ui/button';
import type { DataTableColumn } from '@/shared/components/ui/data-table';
import { DataTable } from '@/shared/components/ui/data-table';
import { PERMISSIONS } from '@/shared/constants/permissions';

interface PaginationMeta {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  firstItemOnPage?: number;
  lastItemOnPage?: number;
}

interface CountriesTableProps {
  data: Country[];
  pagination?: PaginationMeta;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onViewDetail?: (countryId: string) => void;
  onEdit?: (countryId: string) => void;
  onToggle?: (countryId: string) => void;
  onDelete?: (countryId: string) => void;
}

export function CountriesTable({
  data,
  pagination,
  onPageChange,
  onPageSizeChange,
  onViewDetail,
  onEdit,
  onToggle,
  onDelete,
}: CountriesTableProps) {
  const { hasPermission } = useAuthorization();
  const canToggle = hasPermission(PERMISSIONS.COUNTRIES_TOGGLE);

  const columns: DataTableColumn<Country>[] = [
    {
      key: 'officialName',
      header: 'Nombre oficial',
      render: (row) => (
        <span data-testid={`country-name-${row.id}`} className="text-light text-sm">
          {row.officialName}
        </span>
      ),
    },
    {
      key: 'commonName',
      header: 'Nombre común / alias',
      render: (row) => (
        <span data-testid={`country-common-${row.id}`} className="text-primary text-sm">
          {row.commonName}
        </span>
      ),
    },
    {
      key: 'isoCode',
      header: 'Código ISO',
      render: (row) => (
        <span data-testid={`country-isocode-${row.id}`} className="text-primary text-sm">
          {row.isoCode ?? '—'}
        </span>
      ),
    },
    {
      key: 'currency',
      header: 'Moneda base',
      render: (row) => (
        <span data-testid={`country-currency-${row.id}`} className="text-primary text-sm">
          {row.currency}
        </span>
      ),
    },
    {
      key: 'isActive',
      header: 'Estado',
      render: (row) => (
        <div data-testid={`country-status-${row.id}`}>
          <Switch
            checked={row.isActive}
            disabled={!canToggle}
            onCheckedChange={() => onToggle?.(row.id)}
          />
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Acciones',
      render: (row: Country) => (
        <div data-testid={`country-actions-${row.id}`} className="flex items-center gap-2">
          <Protected permissions={PERMISSIONS.COUNTRIES_VIEW}>
            <Button
              data-testid={`view-country-${row.id}`}
              iconImg={<Eye />}
              title="Ver detalle"
              onClick={() => onViewDetail?.(row.id)}
            />
          </Protected>
          <Protected permissions={PERMISSIONS.COUNTRIES_EDIT}>
            <Button
              data-testid={`edit-country-${row.id}`}
              iconImg={<Edit />}
              title="Editar"
              onClick={() => onEdit?.(row.id)}
            />
          </Protected>
          <Protected permissions={PERMISSIONS.COUNTRIES_DELETE}>
            {!row.isActive ? (
              <Button
                data-testid={`delete-country-${row.id}`}
                iconImg={<Trash2 />}
                title="Eliminar"
                variant="default"
                className="text-destructive hover:text-destructive"
                onClick={() => onDelete?.(row.id)}
              />
            ) : (
              <Button
                iconImg={<Trash2 />}
                title="Debe deshabilitarse antes de eliminar"
                variant="default"
                className="text-muted-foreground cursor-not-allowed opacity-50"
                disabled
              />
            )}
          </Protected>
        </div>
      ),
    },
  ];

  return (
    <div data-testid="countries-table">
      <DataTable
        title="Países"
        subtitle={String(data.length)}
        rows={data}
        columns={columns}
        getRowKey={(row) => String(row.id)}
        pagination={pagination}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        testIdPrefix="countries"
      />
    </div>
  );
}
